<template>
  <q-header
    ref="headerRef"
    :class="['web-header bg-white', { 'z-fullscreen': isMobileMenuOpened }]"
  >
    <div
      :class="['container text-black flex items-center q-px-16 q-px-md-none justify-between', {
        'no-wrap': !partnerLogo,
      }]"
    >
      <div
        :class="['flex no-wrap flex-1 q-mr-16', {
          'row reverse justify-end': domain.is.aeroflot,
        }]"
      >
        <template v-if="partnerLogo && isAeroflotSubdomain">
          <a
            :href="partnerLink"
            class="web-header-logo domain-partner-logo flex no-wrap items-center"
            target="_blank"
          >
            <img
              :src="partnerLogo"
              :alt="domain.partnerTitle"
            >
          </a>
          <q-separator
            v-if="!domain.is.aeroflot"
            class="logo-separator self-center"
            vertical
          />
        </template>
        <div
          v-if="domain.is.aeroflot && isAeroflotSubdomain"
          class="partnership gt-sm fs-14 text-weight-medium self-center q-px-8 q-px-md-16"
        >
          {{ $t('domains.aeroflot.in_partnership_with') }}
        </div>
        <component
          :is="logoTag"
          v-bind="logoAttr"
          class="web-header-logo flex items-center"
        >
          <img
            :src="`common/${domain.customLogo || 'logo.svg'}?v=2026b`"
            :alt="domain.brand.title"
          >
        </component>
      </div>
      <div class="web-header-nav flex column no-wrap items-end justify-between">
        <div class="flex no-wrap flex-center">
          <slot name="carrier-ratings" />
          <template v-if="!isCabinet.carrier">
            <q-btn
              v-if="account"
              class="q-mr-16 gt-xs"
              :href="`/${$i18n.locale}/client/#/cabinet/transfers/active`"
              :label="$t('button.my_trips')"
              icon="requests"
              dense
              no-wrap
              flat
            >
              <PassengerInboxBadge />
            </q-btn>
            <Balance class="gt-sm q-ml-16" />
          </template>
          <HeaderSettings :redirect-after-logout="redirectAfterLogout">
            <template #after>
              <Balance
                v-if="!isCabinet.carrier"
                class="lt-md q-mx-16 q-mb-16"
              />
            </template>
          </HeaderSettings>
          <q-btn
            dense
            flat
            :icon="isMobileMenuOpened ? 'close' : 'drag_handle'"
            :class="['burger q-ml-8 q-ml-sm-16', {
              'block': burgerMenu,
            }]"
            @click="handleMenu"
          >
            <component
              :is="$q.screen.lt.md ? 'div' : 'q-menu'"
              v-if="menuOpened"
              v-model="menuOpened"
              :class="['web-header-menu bg-white scroll-y fs-16 text-left', { 'fixed-bottom': $q.screen.lt.md }]"
              :style="{ top: `${$refs.headerRef.$el.offsetHeight}px` }"
              @scroll="onScroll"
            >
              <div>
                <div
                  v-if="domain.hasApp"
                  class="q-pa-16"
                >
                  <LinkApps
                    class="inline-block"
                    :type="isCabinet.carrier ? 'carrier' : 'passenger'"
                  />
                </div>
                <q-list>
                  <q-item
                    v-for="(navLink, navName) in navLinks"
                    :key="navName"
                    v-close-popup
                    clickable
                    tag="a"
                    :href="navLink.href || navLinkWithLocale(navLink)"
                    @click="navigate($event, navLink)"
                  >
                    <q-item-section>
                      {{ $t(`header.nav.${navName}`) }}
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-for="link in legalPages"
                    :key="link"
                    v-close-popup
                    clickable
                    tag="a"
                    :href="`/${$i18n.locale}/${link}`"
                  >
                    <q-item-section>
                      {{ $t(`footer.sitemap.nav.${link}`) }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
            </component>
          </q-btn>
        </div>
        <div
          v-if="!burgerMenu && !isCabinet.carrier && !domain.is.aeroflot"
          class="flex nav-links no-wrap q-mt-16"
        >
          <div
            v-for="(navLink, navName) in navLinks"
            :key="navName"
            class="text-no-wrap"
          >
            <a
              :href="navLink.href || navLinkWithLocale(navLink)"
              @click="navigate($event, navLink)"
            >
              {{ $t(`header.nav.${navName}`) }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </q-header>
</template>

<script setup>
import { computed, inject, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import { storeToRefs } from 'pinia';

import Balance from './Balance';
import LinkApps from './LinkApps';
import HeaderSettings from './HeaderSettings';
import PassengerInboxBadge from './PassengerInboxBadge';

import useNavLinks from '~composables/useNavLinks';
import { useAccountStore } from '~stores/account';

const route = useRoute();
const accountStore = useAccountStore();
const { locale } = useI18n();
const $q = useQuasar();

// Client-side only check for subdomain
const isAeroflotSubdomain = ref(false);
onMounted(() => {
  isAeroflotSubdomain.value = typeof window !== 'undefined' && window.location.hostname.startsWith('aeroflot.');
});

const { navLinks, navLinkWithLocale, legalPages } = useNavLinks();

defineProps({
  redirectAfterLogout: {
    type: String,
    default: null,
  },
  burgerMenu: {
    type: Boolean,
    default: false,
  },
});

const bus = inject('bus');
const isCabinet = inject('isCabinet');
const domain = inject('domain');

const aeroflotLocales = ['en', 'ru', 'de', 'fr', 'es', 'it', 'ja', 'zh'];

const menuOpened = ref(false);

const { account } = storeToRefs(accountStore);

const isIndexPage = computed(() => route.name === 'index');
const logoTag = computed(() => (isIndexPage.value ? 'div' : 'a'));
const logoAttr = computed(() => (isIndexPage.value ? {} : { href: `/${locale.value}` }));

const partnerLogo = computed(() => {
  if (domain.withoutPartnership) return null;

  // One monochrome mark for every language: it is drawn for the dark header.
  if (domain.is.aeroflot) {
    return 'common/domains/aeroflot/logo-ru.svg?v=2026d';
  }

  return `common/domains/${domain.name}/logo.png?v=2026c`;
});

const partnerLink = computed(() => {
  const link = domain.partnerLink;

  if (domain.is.aeroflot) {
    return `${link}${aeroflotLocales.find((el) => el === locale.value) || 'en'}`;
  }

  return link;
});

const isMobileMenuOpened = computed(() => menuOpened.value && $q.screen.lt.md);

function handleMenu() {
  menuOpened.value = !menuOpened.value;
  if ($q.screen.lt.md) {
    document.body.classList.toggle('overflow-hidden', menuOpened.value);
  }
}

function onScroll({ target }) {
  if ($q.screen.lt.md) {
    target.classList.toggle('scrolled', target.scrollTop > 0);
  }
}

function navigate(event, target) {
  if ($q.screen.gt.sm && target === 'support') {
    event.preventDefault();
    bus.emit('show-support');
  }
}
</script>

<style lang="scss" scoped>
.web-header {
  .container {
    @media (min-width: $breakpoint-md-min) {
      width: fit-content;
      min-width: 970px;
    }

    @media (min-width: $breakpoint-lg-min) {
      width: fit-content;
      min-width: 1170px;
    }
  }

  &-logo {
    @media (max-width: $breakpoint-sm-max) {
      height: 60px;
    }

    & > img {
      width: 180px;

      @media (max-width: $breakpoint-sm-max), (max-height: 320px) {
        width: 100px;
      }
    }
  }

  .partnership {
    @media (max-width: $breakpoint-sm-max) {
      font-size: 9px !important;
    }
  }

  &-nav {
    flex: 1;
    margin: 10px 0;

    @media (max-width: $breakpoint-sm-max) {
      margin: 0;
    }
  }

  .nav-links {
    div {
      font-weight: 500;

      &:first-child {
        border-left: none;
      }

      a {
        color: #000;
        text-decoration: none;
        padding: 6px;
      }
    }

    @media (max-width: 1279px) {
      display: none;
    }
  }

  &-menu {
    color: #000 !important;
    text-transform: none;
    font-weight: 400;

    &.scrolled {
      box-shadow: 0 2px 8px 0 rgba(#000, .08) inset;
    }
  }

  @media (min-width: 1280px) {
    .burger {
      display: none;
    }
  }

  :deep(.balance-label) {
    color: rgba(#000, 0.54);
  }
}
</style>
